/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useRef, useState } from 'react'
import {
  DeviceEventCategory,
  DeviceEventType,
  getCategoryByEventType,
  getCategoryName,
  getEventName,
} from '../../types/DeviceEventTypes'
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react'

type Props = {
  selectedTypes: DeviceEventType[]
  eventsToFilterOut: DeviceEventType[]
  setIsDirty: (isDirty: boolean) => void
  setTypes: (prev: any) => void
  category: DeviceEventCategory
  open: boolean
}

function DeviceEventFilterOption({ selectedTypes, eventsToFilterOut, setIsDirty, setTypes, category, open }: Props) {
  const [allMatch, setAllMatch] = useState<boolean>(false)
  const [isCollapsed, setIsCollapsed] = useState<boolean>(!open)
  const [eventsForOrg, setEventsForOrg] = useState<DeviceEventType[]>([])

  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    const filteredEvents = Object.values(DeviceEventType).filter((type) => !eventsToFilterOut.includes(type))
    setEventsForOrg(filteredEvents)
  }, [eventsToFilterOut])

  const handleSelectedCategoriesChange = (eventType: string, isChecked: boolean) => {
    setIsDirty(true)
    const typesForCategory = eventsForOrg.filter(
      (type) => getCategoryByEventType(type) === getCategoryName(eventType as DeviceEventCategory)
    )
    for (const type of typesForCategory) {
      if (isChecked) {
        if (!selectedTypes.includes(type)) {
          setTypes((prev: DeviceEventType[]) => [...new Set([...prev, type])])
        }
      } else {
        setTypes((prev: DeviceEventType[]) => prev.filter((t) => t !== type))
      }
    }
  }

  const handleSelectedTypesChange = (eventType: string) => {
    let updatedCheckedItems: DeviceEventType[]
    if (selectedTypes.includes(eventType as DeviceEventType)) {
      updatedCheckedItems = selectedTypes.filter((item) => item !== eventType)
    } else {
      updatedCheckedItems = [...selectedTypes, eventType as DeviceEventType]
    }
    setIsDirty(true)
    setTypes(updatedCheckedItems)
  }

  useEffect(() => {
    const found = selectedTypes.filter((type) => getCategoryByEventType(type) === getCategoryName(category))
    const allTypes = eventsForOrg.filter((type) => getCategoryByEventType(type) === getCategoryName(category))
    const allMatch = found.length === allTypes.length
    const someMatch = found.length > 0 && allMatch === false

    setAllMatch(allMatch)
    if (inputRef.current) {
      inputRef.current.indeterminate = someMatch
    }
  }, [selectedTypes, eventsForOrg])

  return eventsForOrg.filter((type) => getCategoryByEventType(type) === getCategoryName(category)).length > 0 ? (
    <>
      <div key={category} className="py-1">
        <div className="flex justify-between pb-2">
          <label className="inline-flex h-4 items-center text-sm">
            <input
              key={category}
              id={category}
              type="checkbox"
              ref={inputRef}
              checked={allMatch == true}
              className="h-8 w-8 text-blue-600 border-gray-800 rounded focus:ring-mainPurple [&:indeterminate]:bg-mainPurple checked:bg-mainPurple dark:border-gray-200 dark:bg-transparent dark:checked:bg-mainPurple scale-50"
              onChange={(e) => {
                handleSelectedCategoriesChange(category, e.target.checked)
              }}
            />
            {getCategoryName(category)}
          </label>
          {isCollapsed ? (
            <IconChevronRight
              className="ml-2 cursor-pointer"
              onClick={() => {
                setIsCollapsed(!isCollapsed)
              }}
              width={16}
              height={16}
            />
          ) : (
            <IconChevronDown
              className="ml-2 cursor-pointer"
              onClick={() => {
                setIsCollapsed(!isCollapsed)
              }}
              width={16}
              height={16}
            />
          )}
        </div>
        <div className={`flex flex-col gap-1 pl-6 ${isCollapsed ? 'hidden' : ''}`}>
          {eventsForOrg
            .filter((type) => getCategoryByEventType(type) === getCategoryName(category))
            .map((type: DeviceEventType) => (
              <label key={type} className="flex text-sm">
                <input
                  id={type}
                  type="checkbox"
                  checked={selectedTypes?.includes(type)}
                  onChange={() => {
                    handleSelectedTypesChange(type)
                  }}
                  className="h-8 w-8 text-blue-600 border-gray-800 rounded focus:ring-mainPurple checked:bg-mainPurple dark:border-gray-200 dark:bg-transparent dark:checked:bg-mainPurple scale-50"
                />
                <div className="pt-1">{getEventName(type)}</div>
              </label>
            ))}
        </div>
      </div>
    </>
  ) : null
}
export default DeviceEventFilterOption
